import type {Theme} from "@mui/material/styles";
import type {MaterialHarmonizationAdditionalInfo} from "../../../../services/ApiTypes.ts";

/** Measures chip labels and syncs how many property tags fit in a row for the current theme. */
export class JsonPropertiesTagLayout {
    private static readonly CHIP_EXTRA_HORIZONTAL_PX = 16;

    private static readonly SUBPIXEL_SLACK_PX = 2;

    private readonly measureCtx: CanvasRenderingContext2D | null;

    private readonly gapPx: number;

    constructor(theme: Theme) {
        this.gapPx = parseFloat(theme.spacing(1)) || 8;
        this.measureCtx =
            JsonPropertiesTagLayout.createChipLabelMeasureContext(theme);
    }

    formatPropertyLabel(p: MaterialHarmonizationAdditionalInfo): string {
        const unit = p.unit ? ` ${p.unit}` : "";
        return `${p.name}: ${p.value ?? ""}${unit}`;
    }

    overflowLabel(hiddenCount: number): string {
        return `+${hiddenCount}`;
    }

    tagsContentKey(value: unknown): string {
        if (!Array.isArray(value)) return "";
        return (value as MaterialHarmonizationAdditionalInfo[])
            .map((t) => `${t.id}:${t.name}:${t.value ?? ""}:${t.unit ?? ""}`)
            .join("|");
    }

    /**
     * Horizontal space needed so at least the first property chip and a "+N" overflow chip can show
     * when there are multiple tags; single-tag rows only need that chip.
     */
    minColumnWidthForFirstTagAndOverflow(value: unknown): number {
        const tags = (
            Array.isArray(value) ? value : []
        ) as MaterialHarmonizationAdditionalInfo[];
        if (tags.length === 0) return 0;
        if (tags.length === 1) return this.estimateTagChipWidth(tags[0]);
        return (
            this.estimateTagChipWidth(tags[0]) +
            this.gapPx +
            this.estimateOverflowChipWidth(tags.length - 1) +
            2 * 16 // cell padding
        );
    }

    computeInitialVisibleCount(
        rowEl: HTMLDivElement,
        tagList: MaterialHarmonizationAdditionalInfo[],
    ): number {
        if (!Array.isArray(tagList) || tagList.length === 0) return 0;

        const elWidth = rowEl.clientWidth;
        if (elWidth <= 0) return tagList.length;

        let visibleCount = this.estimateVisibleCount(tagList, elWidth);
        if (visibleCount === 0 && tagList.length === 1) visibleCount = 1;
        return visibleCount;
    }

    shouldDecrementVisibleCount(
        rowEl: HTMLDivElement,
        tagList: MaterialHarmonizationAdditionalInfo[],
        visibleCount: number,
    ): boolean {
        if (visibleCount <= 0) return false;
        if (
            rowEl.scrollWidth <=
            rowEl.clientWidth + JsonPropertiesTagLayout.SUBPIXEL_SLACK_PX
        ) {
            return false;
        }
        return !(tagList.length === 1 && visibleCount === 1);
    }

    private static chipLabelFontString(theme: Theme): string {
        const {caption} = theme.typography;
        const size = '13px'
        const weight = caption.fontWeight ?? 400;
        return `${weight} ${size} ${theme.typography.fontFamily}`;
    }

    private static createChipLabelMeasureContext(
        theme: Theme,
    ): CanvasRenderingContext2D | null {
        if (typeof document === "undefined") return null;
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (!ctx) return null;
        ctx.font = JsonPropertiesTagLayout.chipLabelFontString(theme);
        return ctx;
    }

    private measureChipLabelTextWidth(label: string): number {
        if (this.measureCtx) return this.measureCtx.measureText(label).width;
        return label.length * 9;
    }

    private estimateTagChipWidth(
        tag: MaterialHarmonizationAdditionalInfo,
    ): number {
        return (
            this.measureChipLabelTextWidth(this.formatPropertyLabel(tag)) +
            JsonPropertiesTagLayout.CHIP_EXTRA_HORIZONTAL_PX
        );
    }

    private estimateOverflowChipWidth(hidden: number): number {
        return (
            this.measureChipLabelTextWidth(this.overflowLabel(hidden)) +
            JsonPropertiesTagLayout.CHIP_EXTRA_HORIZONTAL_PX
        );
    }

    /**
     * Greedy fit: tags left-to-right, reserve trailing gap + overflow chip when some tags stay hidden.
     */
    private estimateVisibleCount(
        tags: MaterialHarmonizationAdditionalInfo[],
        availablePx: number,
    ): number {
        if (tags.length === 0 || availablePx <= 0) return 0;

        let used = 0;
        let visible = 0;

        for (let i = 0; i < tags.length; i++) {
            const hiddenAfter = tags.length - i - 1;
            const needOverflow = hiddenAfter > 0;
            const leadingGap = visible > 0 ? this.gapPx : 0;
            const tagW = this.estimateTagChipWidth(tags[i]);
            const overflowBlock = needOverflow
                ? this.gapPx + this.estimateOverflowChipWidth(hiddenAfter)
                : 0;

            if (
                used + leadingGap + tagW + overflowBlock >
                availablePx + JsonPropertiesTagLayout.SUBPIXEL_SLACK_PX
            )
                break;

            used += leadingGap + tagW;
            visible++;
        }

        return visible;
    }
}